function createLocalAnimQueue() {
  return {
    fns: [],
    add(fn) {
      this.fns.push(fn);
    },
    tick(dt) {
      this.fns = this.fns.filter((fn) => fn(dt));
    },
    get active() {
      return this.fns.length > 0;
    },
  };
}

export async function prepareMultiRegionNode(env, spec, registry) {
  const area = env.boardArea();

  const leftWidth = area.w * spec.layout.leftWk;
  const gap = area.w * spec.layout.gapK;
  const rightWidth = area.w - leftWidth - gap;

  const leftFactory = registry.get(spec.left.kind);
  const rightFactory = registry.get(spec.right.kind);

  if (!leftFactory || !rightFactory) {
    throw new Error('MultiRegion: child node factories not found');
  }

  const leftEnv = {
    ...env,
    boardArea: () => ({
      x: area.x,
      y: area.y,
      w: leftWidth,
      h: area.h,
    }),
  };

  const rightEnv = {
    ...env,
    boardArea: () => ({
      x: area.x + leftWidth + gap,
      y: area.y,
      w: rightWidth,
      h: area.h,
    }),
  };

  const leftPacket = await leftFactory.prepare(leftEnv, spec.left.payload);
  const rightPacket = await rightFactory.prepare(rightEnv, spec.right.payload);

  return {
    leftPacket,
    rightPacket,
    leftFactory,
    rightFactory,
    leftSpec: spec.left,
    rightSpec: spec.right,
    leftArea: leftEnv.boardArea(),
    rightArea: rightEnv.boardArea(),
    timeline: spec.timeline,
  };
}

export function commitMultiRegionNode(packet, ctxs, anim) {
  const localAnim = createLocalAnimQueue();
  const rect = ctxs.ink.canvas?.getBoundingClientRect?.();
  const dpr = rect && rect.width > 0 ? Math.max(1, ctxs.ink.canvas.width / rect.width) : 1;
  const makeSurface = () => {
    const surface = document.createElement('canvas');
    surface.width = ctxs.ink.canvas.width;
    surface.height = ctxs.ink.canvas.height;
    const ctx = surface.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { canvas: surface, ctx };
  };
  const leftSurface = makeSurface();
  const rightSurface = makeSurface();
  let t = 0;
  let rightStarted = false;

  packet.leftFactory.commit(
    packet.leftPacket,
    { ink: leftSurface.ctx, interaction: ctxs.interaction },
    localAnim,
    packet.leftSpec.payload
  );

  anim.add((dt) => {
    t += dt;

    if (!rightStarted && t >= packet.timeline.startRightAtSec) {
      packet.rightFactory.commit(
        packet.rightPacket,
        { ink: rightSurface.ctx, interaction: ctxs.interaction },
        localAnim,
        packet.rightSpec.payload
      );
      rightStarted = true;
    }

    localAnim.tick(dt);

    const compositeRegion = (region, surface) => {
      if (!region || !surface?.canvas) return;
      ctxs.ink.save();
      ctxs.ink.beginPath();
      ctxs.ink.rect(region.x, region.y, region.w, region.h);
      ctxs.ink.clip();
      ctxs.ink.clearRect(region.x, region.y, region.w, region.h);
      ctxs.ink.drawImage(
        surface.canvas,
        0,
        0,
        ctxs.ink.canvas.width / dpr,
        ctxs.ink.canvas.height / dpr
      );
      ctxs.ink.restore();
    };

    compositeRegion(packet.leftArea, leftSurface);
    if (rightStarted) {
      compositeRegion(packet.rightArea, rightSurface);
    }

    return localAnim.active || !rightStarted;
  });
}
